// Modal de Fornecedor (Suprimentos) — atende `createSupplier` e
// `updateSupplier` com o MESMO componente; o que separa criação de edição é o
// uuid, não a existência de `selectedObject` (o shell entrega `{}` ao criar, e
// `{}` é verdadeiro).
//
// O que sobrou aqui: o estado do formulário, a hidratação, as duas sub-abas e o
// rodapé que salva. O resto mora em ./supplier:
//   tipos.ts             forma do formulário, leitura do banco, regra de vigência
//   DadosDoFornecedor    a aba de cadastro e o bloco de indicadores calculados
//   PrecosAcordados      a tabela de preço acordado e o formulário de linha
import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import * as Casca from "../../../styles/components/configuration/modals/_casca";
import * as Estilo from "../../../styles/components/configuration/modals/SupplierModal";
import { DadosDoFornecedor } from "./supplier/DadosDoFornecedor";
import { OpcaoDeSku, PrecosAcordados } from "./supplier/PrecosAcordados";
import {
  FormularioDeFornecedor,
  PrecoAcordado,
  SupplierModalProps,
  acharSobreposicao,
  descreverVigencia,
  formularioVazio,
  lerFornecedor,
  lerValorCruDoFornecedor,
  numeroOuNulo,
} from "./supplier/tipos";
import { useModalAcessivel } from "../../../hooks/useModalAcessivel";

const SupplierModal: React.FC<SupplierModalProps> = ({
  selectedObject,
  handleCloseModal,
  handleSave,
  parseInfoValue,
  decryptedInfoToken,
}) => {
  // Esc fecha, o foco fica preso dentro e volta para quem abriu, e o leitor
  // de tela anuncia a janela pelo titulo. Ver hooks/useModalAcessivel.ts.
  const { refDoModal, propsDoDialogo } = useModalAcessivel({
    aoFechar: handleCloseModal,
    idDoTitulo: "titulo-suppliermodal",
  });

  const [form, setForm] = useState<FormularioDeFornecedor>(formularioVazio());
  const [aba, setAba] = useState<"dados" | "precos">("dados");
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<{
    tom: "ok" | "erro" | "atencao";
    texto: string;
  } | null>(null);

  /**
   * Edição ou criação.
   *
   * `fornecedorUuid` é o nome que o payload usa; `supplierUUID` é o nome que a
   * linha da tabela de Fornecedores traz (DynamicTable case 4) e
   * `supplierInfoUuid` é o da linha crua do token. Testar só um dos três faria
   * a edição abrir com cara de criação e salvar um fornecedor novo por cima do
   * que já existe.
   */
  const fornecedorUuid: string | undefined =
    selectedObject?.fornecedorUuid ??
    selectedObject?.supplierUUID ??
    selectedObject?.supplierInfoUuid;

  useEffect(() => {
    setForm(lerFornecedor(selectedObject, parseInfoValue, decryptedInfoToken));
    setAviso(null);
    setAba("dados");
    // `parseInfoValue` vem do shell e é recriada a cada render dele; incluí-la
    // aqui reexecutaria a hidratação por cima do que o usuário está digitando.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedObject]);

  /** Fato de fora: o primeiro envio bem-sucedido é quem valida o número. */
  const whatsappValidado: boolean = useMemo(
    () =>
      lerValorCruDoFornecedor(
        selectedObject,
        parseInfoValue,
        decryptedInfoToken
      ).whatsappValidado === true,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedObject, decryptedInfoToken]
  );

  /**
   * Os SKUs da organização: `organizationInfo` do tipo 1. Sem o filtro entrariam
   * na lista as outras linhas de `organizationInfo`, que não são produto.
   */
  const skus: OpcaoDeSku[] = useMemo(() => {
    const infos = decryptedInfoToken?.organization?.organizationInfo ?? [];
    return infos
      .filter((info: any) => Number(info?.organizationInfoType) === 1)
      .map((info: any) => ({
        uuid: info.organizationInfoUuid,
        codigo: info.organizationInfoName || "(sem código)",
        nome: info.organizationInfoDescription || "",
      }));
  }, [decryptedInfoToken]);

  const handleLocalChange = (
    campo: keyof FormularioDeFornecedor,
    valor: any
  ) => {
    setForm((atual) => ({ ...atual, [campo]: valor }));
  };

  const salvarLinhaDePreco = (linha: PrecoAcordado) => {
    setForm((atual) => {
      const existe = atual.precosAcordados.some(
        (preco) => preco.linhaId === linha.linhaId
      );
      return {
        ...atual,
        precosAcordados: existe
          ? atual.precosAcordados.map((preco) =>
              preco.linhaId === linha.linhaId ? linha : preco
            )
          : atual.precosAcordados.concat(linha),
      };
    });
  };

  const removerLinhaDePreco = (linhaId: string) => {
    setForm((atual) => ({
      ...atual,
      precosAcordados: atual.precosAcordados.filter(
        (preco) => preco.linhaId !== linhaId
      ),
    }));
  };

  const nomeDoSku = (uuid: string): string => {
    const sku = skus.filter((opcao) => opcao.uuid === uuid)[0];
    if (!sku) return uuid || "(sem SKU)";
    return sku.nome ? `${sku.codigo} — ${sku.nome}` : sku.codigo;
  };

  const montarPayload = () => ({
    fornecedorUuid,
    nome: form.nome.trim(),
    cnpj: form.cnpj.trim(),
    contato: form.contato.trim(),
    email: form.email.trim(),
    telefone: form.telefone.trim(),
    endereco: form.endereco.trim(),
    canalPreferido: form.canalPreferido,
    // `whatsappValidado` NÃO entra aqui de propósito. Ele é leitura; mandá-lo
    // de volta é exatamente como um "validado" que ninguém provou entraria no
    // banco e liberaria disparo para um número que nunca respondeu.
    prazoEntregaDias: numeroOuNulo(form.prazoEntregaDias),
    condicoesPagamento: form.condicoesPagamento.trim(),
    pedidoMinimoReais: numeroOuNulo(form.pedidoMinimoReais),
    freteTipo: form.freteTipo,
    // Só o campo do tipo escolhido viaja: guardar os dois deixaria o servidor
    // decidir qual vale.
    freteValor:
      form.freteTipo === "valorFixo" ? numeroOuNulo(form.freteValor) : null,
    freteGratisAcima:
      form.freteTipo === "gratisAcimaDe"
        ? numeroOuNulo(form.freteGratisAcima)
        : null,
    janelaAtendimentoDias: form.janelaAtendimentoDias,
    janelaAtendimentoInicio: form.janelaAtendimentoInicio,
    janelaAtendimentoFim: form.janelaAtendimentoFim,
    diasDeEntrega: form.diasDeEntrega,
    janelaConsolidacaoHoras: numeroOuNulo(form.janelaConsolidacaoHoras),
    precosAcordados: form.precosAcordados.map((preco) => ({
      skuUuid: preco.skuUuid,
      unidadeCompra: preco.unidadeCompra.trim(),
      preco: numeroOuNulo(preco.preco),
      vigenciaInicio: preco.vigenciaInicio,
      vigenciaFim: preco.vigenciaFim || null,
      quantidadeMinima: numeroOuNulo(preco.quantidadeMinima),
      embalagem: numeroOuNulo(preco.embalagem),
    })),
  });

  const handleSubmit = async () => {
    if (!form.nome.trim()) {
      setAviso({ tom: "erro", texto: "O nome do fornecedor é obrigatório." });
      setAba("dados");
      return;
    }

    if (form.freteTipo === "valorFixo" && !form.freteValor.trim()) {
      setAviso({
        tom: "erro",
        texto: "Frete de valor fixo escolhido, mas o valor do frete está vazio.",
      });
      setAba("dados");
      return;
    }
    if (form.freteTipo === "gratisAcimaDe" && !form.freteGratisAcima.trim()) {
      setAviso({
        tom: "erro",
        texto:
          "Frete grátis acima de um valor escolhido, mas o valor está vazio.",
      });
      setAba("dados");
      return;
    }

    const semSku = form.precosAcordados.filter((preco) => !preco.skuUuid);
    if (semSku.length > 0) {
      setAviso({
        tom: "erro",
        texto: `${semSku.length} linha(s) de preço estão sem SKU.`,
      });
      setAba("precos");
      return;
    }

    // Recontada aqui, e não só no formulário de linha: linhas hidratadas do
    // banco nunca passaram por aquela checagem, e um valor gravado torto
    // continuaria torto para sempre.
    const choque = acharSobreposicao(form.precosAcordados);
    if (choque) {
      setAviso({
        tom: "erro",
        texto:
          `Vigências sobrepostas em ${nomeDoSku(choque.skuUuid)}: ` +
          `${descreverVigencia(choque.primeira)} e ` +
          `${descreverVigencia(choque.segunda)} cobrem os mesmos dias. ` +
          "Ajuste uma das duas antes de salvar.",
      });
      setAba("precos");
      return;
    }

    setSalvando(true);
    try {
      const resultado: any = await handleSave(
        fornecedorUuid ? "updateSupplier" : "createSupplier",
        montarPayload()
      );
      // Deu certo? Quem fecha o modal é o shell. Deu errado, o modal fica
      // aberto com o motivo — modal que some é indistinguível de sucesso.
      if (resultado && !resultado.ok) {
        setAviso({ tom: resultado.tom ?? "erro", texto: resultado.texto });
      }
    } finally {
      setSalvando(false);
    }
  };

  return ReactDOM.createPortal(
    <Casca.ModalOverlay>
      <Casca.ModalContent ref={refDoModal} {...propsDoDialogo} large>
        <Casca.ModalHeader>
          <Casca.ModalTitle id="titulo-suppliermodal">
            <Casca.MaterialIcons>local_shipping</Casca.MaterialIcons>
            {fornecedorUuid ? "Editar Fornecedor" : "Novo Fornecedor"}
          </Casca.ModalTitle>
          <Casca.ModalCloseButton onClick={handleCloseModal}>
            <Casca.MaterialIcons>close</Casca.MaterialIcons>
          </Casca.ModalCloseButton>
        </Casca.ModalHeader>

        <Casca.ModalBody>
          <Casca.SideList>
            <Casca.SideListItem
              selected={aba === "dados"}
              onClick={() => setAba("dados")}
            >
              <Casca.MaterialIcons>badge</Casca.MaterialIcons>
              Dados do fornecedor
            </Casca.SideListItem>
            <Casca.SideListItem
              selected={aba === "precos"}
              onClick={() => setAba("precos")}
            >
              <Casca.MaterialIcons>sell</Casca.MaterialIcons>
              Preços acordados ({form.precosAcordados.length})
            </Casca.SideListItem>
          </Casca.SideList>

          <Casca.SideListContent>
            <Casca.FormContainer>
              {aba === "dados" ? (
                <DadosDoFornecedor
                  form={form}
                  onChange={handleLocalChange}
                  whatsappValidado={whatsappValidado}
                />
              ) : (
                <PrecosAcordados
                  precos={form.precosAcordados}
                  skus={skus}
                  onSalvarLinha={salvarLinhaDePreco}
                  onRemover={removerLinhaDePreco}
                />
              )}
            </Casca.FormContainer>
          </Casca.SideListContent>
        </Casca.ModalBody>

        <Casca.ModalFooter>
          {/* O que o Salvar faz, dito antes de ser apertado: o cadastro vai
              numa requisição e CADA linha de preço acordado vai na sua. Se uma
              linha for recusada, o cadastro já está gravado — o aviso abaixo
              nomeia quais linhas ficaram de fora. */}
          <Estilo.NotaDeCampo>
            Salvar envia o cadastro em{" "}
            <code>
              {fornecedorUuid
                ? "PATCH suprimentos/fornecedores/:uuid"
                : "POST suprimentos/fornecedores"}
            </code>{" "}
            e cada linha de preço acordado em uma requisição própria, para{" "}
            <code>suprimentos/precos-acordados</code>. A sobreposição de
            vigência é recusada também pelo servidor.
          </Estilo.NotaDeCampo>

          <Casca.ButtonGroup>
            <Casca.SecondaryButton type="button" onClick={handleCloseModal}>
              <Casca.MaterialIcons>close</Casca.MaterialIcons>
              Cancelar
            </Casca.SecondaryButton>
            <Casca.PrimaryButton
              type="button"
              onClick={handleSubmit}
              disabled={salvando}
            >
              <Casca.MaterialIcons>save</Casca.MaterialIcons>
              {salvando
                ? "Salvando…"
                : fornecedorUuid
                ? "Atualizar Fornecedor"
                : "Criar Fornecedor"}
            </Casca.PrimaryButton>
          </Casca.ButtonGroup>

          {aviso && (
            <Casca.AlertMessage $tom={aviso.tom}>
              {aviso.texto}
            </Casca.AlertMessage>
          )}
        </Casca.ModalFooter>
      </Casca.ModalContent>
    </Casca.ModalOverlay>,
    document.body
  );
};

export default SupplierModal;
